import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  createSurveyForm,
  deleteSurveyForm,
  getSurveyFormById,
  getSurveyForms,
  updateSurveyForm,
} from '../services/survey-form.service';
import { GetSurveyFormsParams, SurveyFormInsertInput, SurveyFormUpdateInput } from '../types/survey-form.types';

const invalidateSurveyFormQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-survey-forms'] });

export const useGetSurveyForms = (params: GetSurveyFormsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-survey-forms', params],
    queryFn: () => getSurveyForms(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetSurveyFormById = (itemId?: string) => {
  return useGlobalQuery({
    queryKey: ['hr-survey-form', itemId],
    queryFn: () => getSurveyFormById(itemId as string),
    enabled: Boolean(itemId),
  });
};

export const useCreateSurveyForm = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: SurveyFormInsertInput) => createSurveyForm(input),
    onSuccess: () => {
      invalidateSurveyFormQueries(queryClient);
      toast({ title: t('SURVEY_FORM_CREATED') });
    },
  });
};

export const useUpdateSurveyForm = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: SurveyFormUpdateInput }) => updateSurveyForm(params),
    onSuccess: () => {
      invalidateSurveyFormQueries(queryClient);
      toast({ title: t('SURVEY_FORM_UPDATED') });
    },
  });
};

export const useDeleteSurveyForm = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => deleteSurveyForm(itemId),
    onSuccess: () => {
      invalidateSurveyFormQueries(queryClient);
      toast({ title: t('SURVEY_FORM_DELETED') });
    },
  });
};
